import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { ArrowLeft, Eye, Pencil, Plus, Trash2 } from 'lucide-react';
import Button from '../../components/ui/Button.jsx';
import DataTable from '../../components/ui/DataTable.jsx';
import FormField from '../../components/ui/FormField.jsx';
import Modal from '../../components/ui/Modal.jsx';
import api from '../../services/api.js';
import { confirmDelete, toastError, toastSuccess } from '../../hooks/useToast.js';
import { useDebouncedValue } from '../../hooks/useDebouncedValue.js';
import {
  ADMIN_DELETE_BTN_CLASS,
  ADMIN_ICON_BTN_CLASS,
  adminPublishStatusClasses,
} from '../../constants/adminUi.js';
import {
  getNormatividadCategoryLabel,
  slugifyNormatividadCategory,
} from '../../constants/normatividadCategories.js';
import { useNormatividadCategories } from '../../hooks/useNormatividadCategories.js';

const inputClass =
  'w-full rounded-xl border border-white/15 bg-slate-950/80 px-4 py-3 text-sm text-slate-100 outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green/40';

const empty = {
  slug: '',
  label: '',
  description: '',
  orden: 0,
  activa: true,
};

export default function NormatividadCategoriasAdminPage() {
  const { categories, reload: reloadCategories } = useNormatividadCategories({ admin: true });
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(15);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 350);
  const [loading, setLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [previewRow, setPreviewRow] = useState(null);
  const [slugTouched, setSlugTouched] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    setError,
    formState: { errors },
  } = useForm({ defaultValues: empty });

  const labelValue = watch('label');

  useEffect(() => {
    if (!modalOpen || editingId || slugTouched) return;
    setValue('slug', slugifyNormatividadCategory(labelValue), { shouldValidate: true });
  }, [labelValue, modalOpen, editingId, slugTouched, setValue]);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get('/v1/normatividad/categorias', {
        params: { page, per_page: perPage, search: debouncedSearch },
      });
      if (data.success) {
        setItems(data.data.items);
        setTotal(data.data.total);
      }
    } catch (e) {
      toastError('Error', e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, debouncedSearch]);

  function openCreate() {
    setEditingId(null);
    setSlugTouched(false);
    reset(empty);
    setModalOpen(true);
  }

  function openEdit(row) {
    setEditingId(row.id);
    setSlugTouched(true);
    reset({
      slug: row.slug ?? '',
      label: row.label ?? '',
      description: row.description ?? '',
      orden: Number(row.orden) || 0,
      activa: Number(row.activa) === 1,
    });
    setModalOpen(true);
  }

  async function onSubmit(values) {
    const payload = {
      label: values.label.trim(),
      description: values.description?.trim() ?? '',
      orden: Number(values.orden) || 0,
      activa: values.activa ? 1 : 0,
    };
    if (!editingId) {
      payload.slug = slugifyNormatividadCategory(values.slug);
    }

    try {
      if (editingId) {
        const { data } = await api.put(`/v1/normatividad/categorias/${editingId}`, payload);
        if (!data.success) throw new Error(data.message);
        toastSuccess('Actualizada', data.message);
      } else {
        const { data } = await api.post('/v1/normatividad/categorias', payload);
        if (!data.success) throw new Error(data.message);
        toastSuccess('Creada', data.message);
      }
      setModalOpen(false);
      await load();
      await reloadCategories();
    } catch (e) {
      const fields = e.payload?.details ?? e.payload?.errors;
      if (fields && typeof fields === 'object') {
        Object.entries(fields).forEach(([key, message]) => {
          if (typeof message === 'string') setError(key, { type: 'server', message });
        });
      }
      toastError('No se pudo guardar', e.message);
    }
  }

  async function onDelete(row) {
    const docs = Number(row.documentos_count) || 0;
    if (docs > 0) {
      toastError(
        'No se puede eliminar',
        `Hay ${docs} documento(s) en esta categoría. Desactívala en su lugar.`
      );
      return;
    }
    const ok = await confirmDelete('¿Eliminar categoría?', row.label);
    if (!ok) return;
    try {
      const { data } = await api.delete(`/v1/normatividad/categorias/${row.id}`);
      if (!data.success) throw new Error(data.message);
      toastSuccess('Eliminada', data.message);
      await load();
      await reloadCategories();
    } catch (e) {
      toastError('Error', e.message);
    }
  }

  async function toggleActive(row) {
    try {
      const { data } = await api.put(`/v1/normatividad/categorias/${row.id}`, {
        label: row.label,
        description: row.description ?? '',
        orden: Number(row.orden) || 0,
        activa: Number(row.activa) === 1 ? 0 : 1,
      });
      if (!data.success) throw new Error(data.message);
      toastSuccess('Estado actualizado', '');
      await load();
      await reloadCategories();
    } catch (e) {
      toastError('Error', e.message);
    }
  }

  const columns = useMemo(
    () => [
      { key: 'orden', label: 'Orden' },
      {
        key: 'slug',
        label: 'Identificador',
        render: (row) => <span className="font-mono text-xs text-slate-600">{row.slug}</span>,
      },
      { key: 'label', label: 'Nombre' },
      {
        key: 'documentos_count',
        label: 'Documentos',
        render: (row) => Number(row.documentos_count) || 0,
      },
      {
        key: 'activa',
        label: 'Estado',
        render: (row) => (
          <button
            type="button"
            onClick={() => toggleActive(row)}
            className={adminPublishStatusClasses(Number(row.activa) === 1)}
          >
            {Number(row.activa) === 1 ? 'Activa' : 'Inactiva'}
          </button>
        ),
      },
      {
        key: 'actions',
        label: '',
        render: (row) => (
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className={ADMIN_ICON_BTN_CLASS}
              title="Vista previa"
              onClick={() => {
                setPreviewRow(row);
                setPreviewOpen(true);
              }}
            >
              <Eye className="h-4 w-4" />
            </button>
            <button
              type="button"
              className={ADMIN_ICON_BTN_CLASS}
              title="Editar"
              onClick={() => openEdit(row)}
            >
              <Pencil className="h-4 w-4" />
            </button>
            <button
              type="button"
              className={ADMIN_DELETE_BTN_CLASS}
              title="Eliminar"
              onClick={() => onDelete(row)}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <div className="mx-auto max-w-6xl space-y-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <Link
            to="/admin/normatividad"
            className="mb-4 inline-flex items-center gap-2 text-sm font-semibold text-[#1acb12] hover:text-[#0d2b7f]"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden />
            Volver a documentos
          </Link>
          <h1 className="font-display text-3xl font-bold text-accent-blue md:text-4xl">
            Categorías de normatividad
          </h1>
          <p className="mt-2 max-w-2xl text-slate-600">
            Administra las secciones en las que se agrupan los documentos del portal. El identificador (slug) no se
            puede cambiar después de crear la categoría.
          </p>
        </div>
        <Button type="button" onClick={openCreate}>
          <Plus className="h-4 w-4" /> Nueva categoría
        </Button>
      </div>

      <DataTable
        columns={columns}
        rows={items}
        loading={loading}
        search={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(1);
        }}
        page={page}
        total={total}
        perPage={perPage}
        onPageChange={setPage}
        emptyText="No hay categorías registradas"
      />

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingId ? 'Editar categoría' : 'Nueva categoría'}
      >
        <form className="grid gap-5" onSubmit={handleSubmit(onSubmit)}>
          <FormField label="Nombre" error={errors.label?.message} required>
            <input className={inputClass} {...register('label', { required: 'Requerido' })} />
          </FormField>

          {!editingId ? (
            <FormField
              label="Identificador (slug)"
              hint="Solo minúsculas, números y guiones. Se usa internamente en los documentos."
              error={errors.slug?.message}
              required
            >
              <input
                className={inputClass}
                {...register('slug', {
                  required: 'Requerido',
                  validate: (v) =>
                    /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slugifyNormatividadCategory(v)) ||
                    'Formato inválido',
                })}
                onChange={(e) => {
                  setSlugTouched(true);
                  setValue('slug', e.target.value, { shouldValidate: true });
                }}
              />
            </FormField>
          ) : (
            <FormField label="Identificador (slug)">
              <input className={`${inputClass} cursor-not-allowed opacity-70`} readOnly {...register('slug')} />
            </FormField>
          )}

          <FormField label="Descripción" error={errors.description?.message}>
            <textarea
              rows={3}
              className={inputClass}
              {...register('description', { maxLength: { value: 500, message: 'Máximo 500 caracteres' } })}
            />
          </FormField>

          <FormField label="Orden en el portal">
            <input
              type="number"
              min={0}
              className={inputClass}
              {...register('orden', { valueAsNumber: true })}
            />
          </FormField>

          <label className="flex items-center gap-3 text-sm text-slate-300">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-accent-lime-dark focus:ring-brand-green"
              {...register('activa')}
            />
            Activa en el portal y en el selector de documentos
          </label>

          <div className="flex flex-wrap gap-3">
            <Button type="submit">{editingId ? 'Guardar cambios' : 'Crear categoría'}</Button>
            <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
              Cancelar
            </Button>
          </div>
        </form>
      </Modal>

      <Modal open={previewOpen} onClose={() => setPreviewOpen(false)} title="Vista previa">
        {previewRow && (
          <div className="space-y-3 text-sm text-slate-300">
            <p>
              <span className="text-slate-500">Nombre:</span> {previewRow.label}
            </p>
            <p>
              <span className="text-slate-500">Slug:</span>{' '}
              <span className="font-mono text-accent-lime">{previewRow.slug}</span>
            </p>
            <p>
              <span className="text-slate-500">Descripción:</span> {previewRow.description || '—'}
            </p>
            <p>
              <span className="text-slate-500">Documentos asociados:</span> {previewRow.documentos_count ?? 0}
            </p>
            <p>
              <span className="text-slate-500">Estado:</span>{' '}
              {Number(previewRow.activa) === 1 ? 'Activa' : 'Inactiva'}
            </p>
          </div>
        )}
      </Modal>

    </div>
  );
}